import { CloseButton, Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { AdjustmentsVerticalIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import _ from "lodash";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heading } from "../components/UI/heading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/UI/table";
import { useBottomMenuStore } from "../context";
import constants from "../utils/constants";

export default function OnDeck() {
  const initialOnDeckData = constants.demoOnDeckData;
  const kegSizeToOunces = constants.KEG_SIZES_TO_OZ;
  const [onDeckData, setOnDeckData] = useState(initialOnDeckData);
  const { setActiveRow } = useBottomMenuStore();
  const navigate = useNavigate();
  const [storageLocation, setStorageLocation] = useState("All");
  const [sliderValues, setSliderValues] = useState(
    _.reduce(
      onDeckData,
      (acc, data) => {
        acc[data.name] = parseInt(data.percentFull);
        return acc;
      },
      {}
    )
  );
  const [tapOunces, setTapOunces] = useState(
    _.reduce(
      onDeckData,
      (acc, data) => {
        acc[data.name] = data.ounces;
        return acc;
      },
      {}
    )
  );

  const handleSliderChange = (name, value) => {
    setSliderValues({
      ...sliderValues,
      [name]: value,
    });
  };

  const getBackgroundColor = (percentFull) => {
    const percent = parseInt(percentFull);
    const redColor = { r: 235, g: 64, b: 52 };
    const greenColor = { r: 83, g: 230, b: 46 };
    const r = Math.round(greenColor.r + ((redColor.r - greenColor.r) * (100 - percent)) / 100);
    const g = Math.round(greenColor.g + ((redColor.g - greenColor.g) * (100 - percent)) / 100);
    const b = Math.round(greenColor.b + ((redColor.b - greenColor.b) * (100 - percent)) / 100);
    return `rgb(${r}, ${g}, ${b})`;
  };

  const handleSave = (name) => {
    const updatedData = _.map(onDeckData, (data) =>
      data.name === name
        ? {
            ...data,
            ounces: parseFloat(tapOunces[name]),
            percentFull: `${((tapOunces[name] / kegSizeToOunces[data.kegSize]) * 100).toFixed(1)}%`,
            // percentFull: `${sliderValues[name]}%`,
            // ounces: ((sliderValues[name] / 100) * kegSizeToOunces[data.kegSize]).toFixed(2),
          }
        : data
    );
    setOnDeckData(updatedData);
  };

  const handleRowClick = (data) => {
    setActiveRow(data);
    navigate(constants.ROUTE_PATHS.EDIT_KEG);
  };

  const handleOunceInputChange = (name, value) => {
    setTapOunces({
      ...tapOunces,
      [name]: value,
    });
  };

  const handleOunceButtonClick = (name, amount) => {
    setTapOunces((prevTapOunces) => {
      const updatedOunces = Math.max(0, prevTapOunces[name] - amount).toFixed(1);
      return {
        ...tapOunces,
        [name]: updatedOunces,
      };
    });
  };

  const filteredData =
    storageLocation === "All"
      ? onDeckData
      : onDeckData.filter((data) => data.storedIn === storageLocation);
  const totalQuantity = _.sumBy(filteredData, "qty");

  return (
    <div className="px-5 bg-gray-50 mx-auto h-full">
      <div className="flex justify-between items-center">
        {/* header */}
        <div className="flex flex-row items-center gap-x-2">
          <Heading>On Deck</Heading>
          <span className="bg-secondaryColor rounded-full h-8 w-8 text-white font-semibold text-center flex items-center justify-center">
            {totalQuantity}
          </span>
          <span className="flex flex-row items-center gap-x-1 w-fit ml-2">
            <label className="mr-2 font-medium text-gray-600 text-sm">Storage Location:</label>
            <select
              id="storageLocation"
              value={storageLocation}
              onChange={(e) => setStorageLocation(e.target.value)}
              className="pl-4 text-sm placeholder-gray-400 text-gray-600 border outline-none border-gray-200 rounded-md w-28 py-1"
            >
              <option value="All">All</option>
              <option value="Cooler">Cooler</option>
              <option value="Cellar">Cellar</option>
            </select>
          </span>
        </div>

        {/* search bar and top level button row */}
        <div className="flex flex-row items-center gap-x-4">
          {/* export buttons */}
          <span className="text-xs text-gray-600 font-medium">
            <button className="mr-2 border-2 border-gray-400 rounded-md px-2 py-1 hover:bg-gray-400 hover:text-white">
              Export CSV
            </button>
            <button className="border-2 border-gray-400 rounded-md px-2 py-1 hover:bg-gray-400 hover:text-white">
              Export PDF
            </button>
          </span>

          {/* search bar */}
          <div className="flex bg-white py-2 px-4 sticky top-0 z-0 text-sm text-gray-600 border border-gray-200 mb-2 rounded-md w-64 mt-2">
            <span className="absolute inset-y-0 left-0 pl-2 py-4 text-sm flex items-center text-gray-400">
              <MagnifyingGlassIcon className="w-4" />
            </span>
            <input
              type="text"
              className={"w-full pl-4 text-sm outline-none placeholder-gray-400"}
              placeholder="Search"
              onChange={() => {}}
              autoComplete="off"
            />
          </div>
        </div>
      </div>

      {/* on tap table list */}
      <Table grid striped denser className={"overflow-visible"}>
        <TableHead>
          <TableRow>
            <TableHeader>Qty</TableHeader>
            <TableHeader>Name</TableHeader>
            <TableHeader>Style</TableHeader>
            <TableHeader>Stored In</TableHeader>
            <TableHeader>Type</TableHeader>
            <TableHeader>ABV</TableHeader>
            <TableHeader>% Full / Oz Left</TableHeader>
            <TableHeader>Keg Size</TableHeader>
            <TableHeader>Producer</TableHeader>
            <TableHeader>Category</TableHeader>
            <TableHeader>Date Put On</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {_.map(filteredData, (data) => {
            return (
              <TableRow key={data.name} className={"cursor-pointer hover:bg-gray-200"}>
                <TableCell className="w-5 px-auto">{data.qty}</TableCell>
                <TableCell onClick={() => handleRowClick(data)}>{data.name}</TableCell>
                <TableCell>{data.style}</TableCell>
                <TableCell>{data.storedIn}</TableCell>
                <TableCell>{data.type}</TableCell>
                <TableCell className="w-5">{data.abv}</TableCell>
                <TableCell
                  style={{
                    backgroundColor: getBackgroundColor(data.percentFull),
                  }}
                >
                  <>
                    <div className="flex justify-between items-center">
                      {data.percentFull} / {data.ounces}
                      <Popover as="div" className="relative">
                        <PopoverButton className="flex cursor-pointer w-5 h-5 p-0.5 items-center border border-gray-600 rounded-md bg-gray-50">
                          <AdjustmentsVerticalIcon className="text-gray-800" />
                        </PopoverButton>
                        <PopoverPanel className="absolute right-0 z-50 w-56 origin-top-right rounded-md bg-white py-1 shadow-lg">
                          <>
                            <div className="flex flex-col px-4 py-2 text-sm text-gray-700">
                              Adjust Keg Level
                              <span className="flex flex-row items-center">
                                <input
                                  type="number"
                                  min="0"
                                  value={tapOunces[data.name]}
                                  onChange={(e) => {
                                    handleOunceInputChange(data.name, e.target.value);
                                  }}
                                  className="w-16 min-w-12 my-2 border border-gray-300 rounded-md px-1 text-sm"
                                />
                                <span className="w-fit ml-2">oz</span>
                                {/* <input
                                      type="range"
                                      min="0"
                                      max="100"
                                      value={sliderValues[data.name]}
                                      onChange={(e) => {
                                        handleSliderChange(data.name, e.target.value);
                                      }}
                                      className="w-full my-2"
                                    />
                                    <span className="w-fit ml-2">{sliderValues[data.name]}%</span>
                                */}
                              </span>
                              <div className="flex flex-row gap-x-1 justify-center">
                                <button
                                  className="ounce-reducer"
                                  onClick={() => handleOunceButtonClick(data.name, 1)}
                                >
                                  -1oz
                                </button>
                                <button
                                  className="ounce-reducer"
                                  onClick={() => handleOunceButtonClick(data.name, 5)}
                                >
                                  -5oz
                                </button>
                                <button
                                  className="ounce-reducer"
                                  onClick={() => handleOunceButtonClick(data.name, 12)}
                                >
                                  -12oz
                                </button>
                                <button
                                  className="ounce-reducer"
                                  onClick={() => handleOunceButtonClick(data.name, 16)}
                                >
                                  -16oz
                                </button>
                                <button
                                  className="ounce-reducer"
                                  onClick={() => handleOunceButtonClick(data.name, 20)}
                                >
                                  -20oz
                                </button>
                              </div>
                              <span className="text-xs text-gray-500 mt-2">
                                * 1% = {kegSizeToOunces[data.kegSize] / 100} oz
                              </span>
                              <CloseButton
                                onClick={() => handleSave(data.name)}
                                className="bg-secondaryColor text-white px-2 py-0.5 rounded-md self-end"
                              >
                                Save
                              </CloseButton>
                            </div>
                          </>
                        </PopoverPanel>
                      </Popover>
                    </div>
                  </>
                </TableCell>
                <TableCell>{data.kegSize}</TableCell>
                <TableCell>{data.producer}</TableCell>
                <TableCell>{data.category}</TableCell>
                <TableCell>{data.datePutOn}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
